import { useRef } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import {
  getReviewsByTags,
  getCityReviews,
  getHomepageReviews,
  type Review,
} from "@/data/reviewData";
import { useGoogleReviews } from "@/hooks/useGoogleReviews";
import { REVIEW_STATS, formatRating } from "@/data/reviewStats";
import QuoteModal from "@/components/QuoteModal";

/* ── Google "G" icon (official colors) ── */
const GoogleIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

interface GoogleReviewsCarouselProps {
  /** Tags for contextual review matching (e.g. ["installation", "refinishing"]) */
  contextTags?: string[];
  /** City slug for city-page matching (e.g. "northville") */
  citySlug?: string;
  /** Number of review cards to show */
  count?: number;
  /** Custom heading JSX */
  heading?: React.ReactNode;
  /** Extra review cards appended after the matched reviews */
  extraReviews?: Review[];
}

const GoogleReviewsCarousel = ({
  contextTags,
  citySlug,
  count = 6,
  heading,
  extraReviews,
}: GoogleReviewsCarouselProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const { data: googleData } = useGoogleReviews(sectionRef);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Get contextually matched reviews from curated data
  let reviews: Review[];
  if (citySlug) {
    reviews = getCityReviews(citySlug, count);
  } else if (contextTags?.length) {
    reviews = getReviewsByTags(contextTags, count);
  } else {
    reviews = getHomepageReviews(count);
  }
  if (extraReviews?.length) {
    reviews = [...reviews, ...extraReviews];
  }

  // Live aggregate rating from Google (with fallback)
  const rating = formatRating(googleData?.rating ?? REVIEW_STATS.rating);
  // Never show a lower count than the manually verified static value.
  const reviewCount = Math.max(Number(googleData?.reviewCount) || 0, REVIEW_STATS.count);

  // NOTE: aggregateRating schema is emitted ONCE, canonically, by the
  // prerender step (scripts/generate-seo-html.mjs) using the build-time live
  // count. This component must never inject schema — single emitter only.

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.8;
    scrollRef.current.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <section ref={sectionRef} className="bg-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12">
        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-10 md:mb-14"
        >
          <div className="flex items-center gap-3 mb-4">
            <GoogleIcon />
            <p className="text-[11px] font-bold tracking-[.22em] uppercase text-primary-foreground/50">
              Google Reviews
            </p>
          </div>

          <h2 className="font-display text-[clamp(26px,4vw,44px)] font-semibold text-primary-foreground leading-[1.08]">
            {heading || (
              <>
                What your neighbors are{" "}
                <em className="italic font-normal text-primary">saying</em>
              </>
            )}
          </h2>

          {/* Aggregate rating */}
          <div className="flex items-center gap-5 mt-6">
            <span className="font-display text-6xl md:text-7xl font-semibold text-primary-foreground leading-none">
              {rating}
            </span>
            <div className="flex flex-col gap-1.5">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-[hsl(var(--gold-400))] text-[hsl(var(--gold-400))]"
                  />
                ))}
              </div>
              <span className="text-[12px] text-primary-foreground/50">
                {reviewCount} Google Reviews
              </span>
            </div>
          </div>
        </motion.div>

        {/* ── Carousel controls ── */}
        <div className="flex justify-end gap-2 mb-4">
          <button
            onClick={() => scroll("left")}
            className="w-10 h-10 rounded-full border border-primary-foreground/10 flex items-center justify-center text-primary-foreground/40 hover:text-primary-foreground hover:border-primary-foreground/30 transition-colors"
            aria-label="Previous reviews"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => scroll("right")}
            className="w-10 h-10 rounded-full border border-primary-foreground/10 flex items-center justify-center text-primary-foreground/40 hover:text-primary-foreground hover:border-primary-foreground/30 transition-colors"
            aria-label="Next reviews"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* ── Glassmorphism review cards ── */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 md:-mx-0 md:px-0"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {reviews.map((rev, i) => (
            <motion.div
              key={rev.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="flex-shrink-0 w-[320px] md:w-[380px] snap-start backdrop-blur-xl bg-primary-foreground/[.04] border border-primary-foreground/[.08] rounded-[20px] p-7 md:p-9 hover:bg-primary-foreground/[.07] hover:border-primary-foreground/[.15] transition-all duration-300 flex flex-col"
            >
              {/* Google verified badge */}
              <div className="flex items-center gap-2 mb-4">
                <GoogleIcon className="w-4 h-4 flex-shrink-0" />
                <span className="text-[10px] font-bold tracking-[.14em] uppercase text-primary-foreground/30">
                  Verified Review
                </span>
              </div>

              {/* Gold stars */}
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star
                    key={j}
                    className="w-3.5 h-3.5 fill-[hsl(var(--gold-400))] text-[hsl(var(--gold-400))]"
                  />
                ))}
              </div>

              {/* Review body */}
              <p className="font-display italic text-[14px] md:text-[15px] text-primary-foreground/80 leading-[1.7] mb-5 flex-1">
                "{rev.quote}"
              </p>

              {/* Author */}
              <div className="pt-4 border-t border-primary-foreground/[.06] mt-auto">
                <div className="text-[13px] font-semibold text-primary-foreground">
                  {rev.name}
                </div>
                {rev.loc?.trim() ? (
                  <div className="text-[11px] text-primary-foreground/40 mt-0.5">
                    {rev.loc}
                  </div>
                ) : null}
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── Footer links ── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8">
          <a
            href="https://www.google.com/search?q=1+Wood+Floors+Livonia+MI+reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] text-primary-foreground/70 hover:text-primary-foreground transition-colors inline-flex items-center gap-1.5"
          >
            Read all {reviewCount} reviews on Google →
          </a>

          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="tel:2483258853"
              className="bg-primary-foreground text-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center"
            >
              📞 Call (248) 325-8853
            </a>
            <QuoteModal
              trigger={
                <button className="border border-primary-foreground/20 text-primary-foreground px-6 py-3 rounded-full text-sm font-medium hover:bg-primary-foreground/10 transition-colors text-center">
                  Schedule a Consultation
                </button>
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsCarousel;
